import { Component } from '@theme/component';
import { debounce, prefersReducedMotion } from '@theme/utilities';
import { getScrollContainer } from '@theme/scroll-container';

/**
 * Anchor tab bar with scroll-spy for the membership page.
 *
 * Progressive enhancement: the tabs are ordinary in-page links and the bar is
 * made sticky by CSS, so with no JavaScript every tab still jumps to its
 * section. This class adds three things — a smooth scroll that stops clear of
 * the sticky bar, the active state as sections pass under it, and pulling the
 * active tab into view in the horizontal scroller.
 *
 * Detection is an IntersectionObserver over a thin band directly beneath the
 * bar, so a section registers at the moment it crosses the bar whichever way
 * the page is moving. The two cases a band cannot catch are the foot of the page,
 * where a short final section never reaches it, and the area above the first
 * section, where no tab applies; those are what the scroll
 * listener is for.
 *
 * @extends {Component<{
 *   scroller: HTMLElement,
 *   links: HTMLAnchorElement[],
 * }>}
 */
class MembershipNavComponent extends Component {
  /** Height of the detection band under the bar, in px. */
  static bandHeight = 140;

  /**
   * Where to find a tab's section when no element carries its id — sections
   * without an Anchor ID setting (the generic `section` holding the FAQ and
   * referral content), or one whose Anchor ID was left empty.
   * @type {Record<string, () => Element | null | undefined>}
   */
  static fallbacks = {
    'how-it-works': () => document.querySelector('main [id^="shopify-section-"][id*="__membership_how_it_works"]'),
    // Section keys from templates/page.memberships.json first, the heading as a backup.
    faqs: () =>
      document.querySelector('main [id^="shopify-section-"][id$="__section_6NjT8Q"]') ??
      MembershipNavComponent.sectionByHeading(/membership questions|faq/i),
    // Referral is the closing section, and earlier sections can mention referrals too.
    referral: () =>
      document.querySelector('main [id^="shopify-section-"][id$="__section_n9eitg"]') ??
      MembershipNavComponent.sectionByHeading(/referral program/i, true),
  };

  /**
   * The first (or last) page section with a heading matching the pattern.
   *
   * @param {RegExp} pattern - Tested against each heading's text.
   * @param {boolean} [last] - Return the last match instead of the first.
   */
  static sectionByHeading(pattern, last = false) {
    const sections = Array.from(document.querySelectorAll('main .shopify-section'));

    return sections[last ? 'findLast' : 'find'](
      (section) =>
        !section.querySelector('membership-nav-component') &&
        Array.from(section.querySelectorAll('h1, h2, h3')).some((heading) => pattern.test(heading.textContent ?? ''))
    );
  }

  /**
   * The section a tab points at: the element with its id, otherwise its fallback.
   *
   * @param {HTMLAnchorElement} link - The tab.
   * @returns {HTMLElement | null}
   */
  static resolveTarget(link) {
    const id = decodeURIComponent(link.hash.slice(1));

    if (!id) return null;

    const target = document.getElementById(id) ?? MembershipNavComponent.fallbacks[id]?.() ?? null;

    return target instanceof HTMLElement ? target : null;
  }

  /** @type {IntersectionObserver | undefined} */
  #observer;

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  /** @type {AbortController | undefined} */
  #controller;

  /** Link / target pairs, in document order, for links whose target exists. @type {{link: HTMLAnchorElement, target: HTMLElement}[]} */
  #entries = [];

  /** Targets currently inside the band. @type {Set<HTMLElement>} */
  #inBand = new Set();

  /** @type {HTMLAnchorElement | null} */
  #active = null;

  /** True while a tab click's smooth scroll is running, so the sections it passes do not take the marker. */
  #isNavigating = false;

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #navigateTimer;

  connectedCallback() {
    super.connectedCallback();

    const { links } = this.refs;

    if (!Array.isArray(links) || links.length === 0) return;

    // A tab whose section is not on the page stays a plain link rather than
    // becoming a control that highlights nothing.
    this.#entries = links.flatMap((link) => {
      const target = MembershipNavComponent.resolveTarget(link);

      return target ? [{ link, target }] : [];
    });

    if (this.#entries.length === 0) return;

    this.#controller = new AbortController();

    const { signal } = this.#controller;

    // Only ever used to catch the foot of the page; see the class comment.
    document.addEventListener('scroll', this.#handleScrollEnd, { passive: true, capture: true, signal });
    document.addEventListener('scrollend', this.#handleScrollSettled, { capture: true, signal });
    document.addEventListener('shopify:block:select', this.#handleBlockSelect, { signal });

    // The bar's own height and its sticky offset both feed the band, and both
    // change with the viewport — this fires on width changes as well as height.
    this.#resizeObserver = new ResizeObserver(this.#rebuild);
    this.#resizeObserver.observe(this);

    this.#observe();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#observer?.disconnect();
    this.#observer = undefined;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = undefined;
    this.#controller?.abort();
    this.#controller = undefined;
    this.#inBand.clear();
    this.#entries = [];
    this.#active = null;
    this.#isNavigating = false;
    clearTimeout(this.#navigateTimer);
  }

  /**
   * Scrolls to the tab's section, stopping clear of the sticky bar.
   *
   * @param {Event} event - The click event, delegated from `on:click`.
   */
  navigate(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#"]') : null;

    if (!(link instanceof HTMLAnchorElement)) return;

    const target =
      this.#entries.find((entry) => entry.link === link)?.target ?? MembershipNavComponent.resolveTarget(link);

    // No target means nothing to scroll to, so let the browser do whatever it
    // would have done with the link.
    if (!target) return;

    event.preventDefault();

    // Works the same whether the section is below or already scrolled past
    // above: the distance is measured from where the page is now.
    const container = getScrollContainer();
    const containerTop = container === document.scrollingElement ? 0 : container.getBoundingClientRect().top;
    const top = target.getBoundingClientRect().top - containerTop + container.scrollTop - this.#offset();

    // 'instant' rather than 'auto', which would pick up the stylesheet's
    // `scroll-behavior: smooth` and animate anyway.
    container.scrollTo({ top: Math.max(0, top), behavior: prefersReducedMotion() ? 'instant' : 'smooth' });

    // Keyboard and screen reader users follow a jump link with the caret, so
    // move it to the destination the way a native anchor jump would.
    target.setAttribute('tabindex', '-1');
    target.focus({ preventScroll: true });

    history.replaceState(null, '', link.hash);

    this.#setActive(link);

    // Hold the marker on the clicked tab until the smooth scroll settles. The
    // timer covers browsers without `scrollend` and a scroll that never moves.
    this.#isNavigating = true;
    clearTimeout(this.#navigateTimer);
    this.#navigateTimer = setTimeout(this.#finishNavigate, 1200);
  }

  #handleScrollSettled = (/** @type {Event} */ event) => {
    if (this.#isPageScroll(event)) this.#finishNavigate();
  };

  #finishNavigate = () => {
    if (!this.#isNavigating) return;

    this.#isNavigating = false;
    clearTimeout(this.#navigateTimer);
    this.#sync();
  };

  /** Marks the tab for whichever section is under the bar now. */
  #sync() {
    // The foot of the page comes first: a short final section can never scroll
    // up to the band, so once the page is at its end and that section is on
    // screen it owns the tab, even if an earlier section still fills the band.
    const last = this.#entries[this.#entries.length - 1];

    if (last && this.#isAtPageEnd() && last.target.getBoundingClientRect().top < innerHeight) {
      this.#setActive(last.link);
      return;
    }

    // Two sections can share the band mid-transition; the upper one owns the
    // tab, so the marker never flip-flops between them.
    const current = this.#entries.find(({ target }) => this.#inBand.has(target));

    if (current) {
      this.#setActive(current.link);
      return;
    }

    // Back above the first tabbed section (the hero and intro), no tab applies.
    const first = this.#entries[0]?.target;

    if (first && first.getBoundingClientRect().top > this.#offset()) {
      this.#setActive(null);
      return;
    }

    // Otherwise the page is somewhere inside the section that is already
    // marked, so the last state stands.
  }

  #isAtPageEnd() {
    const container = getScrollContainer();

    return container.scrollTop + container.clientHeight >= container.scrollHeight - 2;
  }

  /**
   * Scroll events from the page itself. On desktop (≥990px) Horizon scrolls
   * `.page-wrapper` rather than the window, and an element's scroll events do
   * not bubble, so the listeners capture at the document and ignore carousels
   * and other inner scrollers here.
   *
   * @param {Event} event - A `scroll` or `scrollend` event.
   */
  #isPageScroll(event) {
    return event.target === document || event.target === getScrollContainer();
  }

  /** Distance from the top of the viewport to the underside of the stuck bar. */
  #offset() {
    // The section wrapper carries `position: sticky`, because the bar itself is
    // only as tall as its own section and would have nothing to stick within.
    const wrapper = this.closest('.shopify-section') ?? this;
    const top = parseFloat(getComputedStyle(wrapper).top);

    return (Number.isFinite(top) ? top : 0) + this.offsetHeight;
  }

  #observe() {
    this.#observer?.disconnect();
    this.#inBand.clear();

    const offset = this.#offset();
    const band = Math.min(MembershipNavComponent.bandHeight, Math.max(1, innerHeight - offset - 1));
    const bottom = Math.max(0, innerHeight - offset - band);

    this.#observer = new IntersectionObserver(this.#handleIntersect, {
      rootMargin: `-${offset}px 0px -${bottom}px 0px`,
      threshold: 0,
    });

    for (const { target } of this.#entries) this.#observer.observe(target);
  }

  #rebuild = debounce(() => {
    if (this.isConnected) this.#observe();
  }, 150);

  /** @type {IntersectionObserverCallback} */
  #handleIntersect = (entries) => {
    for (const entry of entries) {
      const target = /** @type {HTMLElement} */ (entry.target);

      if (entry.isIntersecting) {
        this.#inBand.add(target);
      } else {
        this.#inBand.delete(target);
      }
    }

    if (!this.#isNavigating) this.#sync();
  };

  /** Catches what the band cannot: the foot of the page and the area above the first section. */
  #handleScrollEnd = (/** @type {Event} */ event) => {
    if (!this.#isPageScroll(event) || this.#isNavigating) return;
    if (this.#inBand.size > 0 && !this.#isAtPageEnd()) return;

    this.#sync();
  };

  /**
   * @param {Event} event - The `shopify:block:select` event; its target is the block element.
   */
  #handleBlockSelect = (event) => {
    const { target } = event;

    if (!(target instanceof HTMLElement) || !this.contains(target)) return;

    const link = target.querySelector('a');

    if (link instanceof HTMLAnchorElement) this.#setActive(link);
  };

  /** @param {HTMLAnchorElement | null} link - The tab to mark, or null to clear. */
  #setActive(link) {
    if (link === this.#active) return;

    this.#active?.classList.remove('membership-nav__link--active');
    this.#active?.removeAttribute('aria-current');

    this.#active = link;

    if (!link) return;

    link.classList.add('membership-nav__link--active');
    link.setAttribute('aria-current', 'true');

    this.#revealActive(link);
  }

  /** Brings the marked tab into view in the horizontal scroller. */
  #revealActive(link) {
    const { scroller } = this.refs;

    if (!(scroller instanceof HTMLElement) || scroller.scrollWidth <= scroller.clientWidth) return;

    // scrollIntoView would also scroll the page vertically, which would fight
    // the scroll that triggered this in the first place.
    const left = link.offsetLeft - (scroller.clientWidth - link.offsetWidth) / 2;

    scroller.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
  }
}

if (!customElements.get('membership-nav-component')) {
  customElements.define('membership-nav-component', MembershipNavComponent);
}
